import { Application } from 'pixi.js';
import { SCENE_H, SCENE_W, defaultConfig, type GameEvent } from '@rekrut/shared';
import { Engine } from './sim/engine.js';
import { applyEvent, bindWeapons } from './sim/economy.js';
import { Renderer } from './render/renderer.js';
import { MockEventSource } from './sources/mock.js';
import { SocketEventSource } from './sources/socket.js';
import type { IEventSource } from './sources/types.js';
import { DevPanel } from './dev/panel.js';
import { Sound } from './audio/sound.js';

const params = new URLSearchParams(location.search);
/** Комната стримера: один сервер обслуживает много эфиров одновременно. */
const streamerId = params.get('room') ?? 'demo';
const useMock = params.get('src') !== 'socket';
const showDev = params.get('dev') !== '0';

async function main(): Promise<void> {
  const app = new Application();
  await app.init({
    width: SCENE_W,
    height: SCENE_H,
    background: 0x05070c,
    antialias: true,
    // WebGPU в CEF внутри OBS нет — просим WebGL явно, иначе на части сборок
    // Pixi 8 уходит в тихий фолбэк или чёрный экран.
    preference: 'webgl',
    powerPreference: 'high-performance',
    resolution: 1,
    autoDensity: false,
  });

  const stage = document.getElementById('stage')!;
  stage.appendChild(app.canvas);

  /**
   * Подгонка сцены под экран.
   *
   * По умолчанию вписываем целиком: ничего не теряется, но на телефонах уже
   * 9:16 остаются поля по бокам. `?fit=cover` растягивает до заполнения,
   * срезая края, — для трансляции экрана это обычно нужнее, чем чёрные полосы.
   */
  const cover = params.get('fit') === 'cover';

  /**
   * Ориентация кадра.
   *
   * TikTok в режиме трансляции экрана заворачивает вертикальную картинку
   * телефона в горизонтальный кадр и добивает её полосами по бокам. Заполнить
   * кадр можно только одним способом: повернуть телефон и отдать игру
   * горизонтальной. Поэтому сцена умеет обе ориентации и переключается сама
   * по форме окна; `?land=1` и `?land=0` фиксируют режим вручную.
   *
   * Симуляция при этом не меняется вообще — поворачивается только картинка.
   */
  const landParam = params.get('land');
  let landscape = false;
  const viewW = (): number => (landscape ? SCENE_H : SCENE_W);
  const viewH = (): number => (landscape ? SCENE_W : SCENE_H);

  const fit = (): void => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Обрезка нужна только в портрете: там сцена 9:16, а экраны телефонов
    // длиннее, и полосы по бокам заметны. В альбоме сцена уже 16:9 — обрезать
    // нечего, а на вытянутом экране cover срезал бы верхнюю панель с
    // названиями команд, то есть ровно то, ради чего зритель смотрит первые
    // три секунды.
    const k =
      cover && !landscape
        ? Math.max(vw / viewW(), vh / viewH())
        : Math.min(vw / viewW(), vh / viewH());
    app.canvas.style.width = `${Math.round(viewW() * k)}px`;
    app.canvas.style.height = `${Math.round(viewH() * k)}px`;
  };
  fit();

  // Полноэкранный режим браузер разрешает только по касанию, поэтому кнопка.
  const fsButton = document.getElementById('fullscreen') as HTMLButtonElement | null;
  const goFullscreen = async (): Promise<void> => {
    try {
      await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
      // Блокировка ориентации есть не везде и не описана в типах — зовём
      // осторожно, отсутствие не должно ронять переход в полный экран.
      const orientation = screen.orientation as unknown as {
        lock?: (o: string) => Promise<void>;
      };
      // Ориентацию не фиксируем: горизонтальный режим — рабочий, поворот
      // телефона это осознанный выбор ведущего под кадр TikTok.
      await orientation?.lock?.(landParam === '1' ? 'landscape' : 'any').catch(() => {});
    } catch {
      // Настольный браузер может отказать — не беда, кнопку всё равно убираем.
    }
    fsButton?.classList.add('hidden');
    setTimeout(applyOrientation, 400);
  };
  fsButton?.addEventListener('click', () => void goFullscreen());
  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement) fsButton?.classList.remove('hidden');
    setTimeout(applyOrientation, 200);
  });
  // В OBS и на десктопе кнопка ни к чему: там окно и так нужного размера.
  if (showDev || window.innerWidth > 900) fsButton?.classList.add('hidden');

  // Потеря контекста на 24/7 — вопрос времени: драйвер, смена сцены в OBS,
  // ресет GPU. Без этого обработчика игра просто молча умирает посреди эфира.
  app.canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    console.warn('[rekrut] WebGL context lost — перезагрузка страницы');
    location.reload();
  });

  const cfg = structuredClone(defaultConfig);
  // Тема матча задаётся ссылкой: ?a=РОНАЛДУ&b=МЕССИ&aimg=...&bimg=...
  // Стримеру не нужно ничего пересобирать, чтобы сменить противостояние.
  cfg.teams.A.label = params.get('a') ?? cfg.teams.A.label;
  cfg.teams.B.label = params.get('b') ?? cfg.teams.B.label;
  cfg.teams.A.imageUrl = params.get('aimg') ?? cfg.teams.A.imageUrl;
  cfg.teams.B.imageUrl = params.get('bimg') ?? cfg.teams.B.imageUrl;

  const engine = new Engine(cfg);
  bindWeapons(engine);
  const renderer = new Renderer(app, engine, cfg);

  /** Пересобрать кадр под текущую форму окна. */
  const applyOrientation = (): void => {
    const want =
      landParam === '1' ? true : landParam === '0' ? false : window.innerWidth > window.innerHeight;
    if (want !== landscape) {
      landscape = want;
      app.renderer.resize(viewW(), viewH());
      renderer.setLandscape(landscape);
    }
    fit();
  };
  applyOrientation();
  window.addEventListener('resize', applyOrientation);
  // На телефоне адресная строка съезжает уже после resize — пересчитываем
  // ещё и по смене размеров видимой области.
  window.visualViewport?.addEventListener('resize', applyOrientation);
  screen.orientation?.addEventListener('change', () => setTimeout(applyOrientation, 300));

  // Звук: ?sound=0 выключает, ?vol=0.3 задаёт громкость. В OBS контекст
  // стартует сам, в обычном браузере — после первого действия пользователя,
  // поэтому дополнительно поднимаем его по первому клику или клавише.
  const sound = new Sound({
    enabled: params.get('sound') !== '0',
    volume: Number(params.get('vol') ?? 0.5),
  });
  renderer.setSound(sound);
  // Слушаем до тех пор, пока контекст реально не запустится: первый клик
  // может прийти раньше, чем браузер разрешит звук, и одноразовый
  // обработчик тогда пропадает впустую.
  const wake = (): void => {
    sound.resume();
    if (sound.running) {
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('keydown', wake);
      window.removeEventListener('touchstart', wake);
    }
  };
  window.addEventListener('pointerdown', wake);
  window.addEventListener('keydown', wake);
  window.addEventListener('touchstart', wake);
  sound.resume();
  if (cfg.teams.A.imageUrl) renderer.setTeamImage('A', cfg.teams.A.imageUrl);
  if (cfg.teams.B.imageUrl) renderer.setTeamImage('B', cfg.teams.B.imageUrl);

  const mock = new MockEventSource();
  const source: IEventSource = useMock
    ? mock
    : new SocketEventSource({
        url: params.get('url') ?? `ws://${location.hostname}:3000/overlay`,
        streamerId,
      });

  // Аватарка заказывается сразу, как человек попал в участники.
  engine.onAvatarNeeded = (userId, url) => renderer.preloadAvatar(userId, url);

  source.onEvent((ev: GameEvent) => applyEvent(engine, ev));
  source.onStatus((st) => console.info('[rekrut] источник:', source.name, st));
  await source.connect(streamerId);
  renderer.setGiftCatalog(source.giftCatalog);

  engine.onMatchEnd = (state) => {
    // Сюда сядет отправка результатов на бэкенд ('match:finished', §9.2).
    console.info('[rekrut] матч завершён', {
      matchId: state.matchId,
      winner: state.winner,
      reason: state.endReason,
      participants: state.participants.size,
    });
  };

  let speed = 1;
  const panel = showDev
    ? new DevPanel(engine, renderer, mock, sound, {
        getSpeed: () => speed,
        setSpeed: (v) => (speed = v),
        getFps: () => app.ticker.FPS,
      })
    : null;
  if (!showDev) document.getElementById('devpanel')!.classList.add('hidden');

  window.addEventListener('keydown', (e) => {
    if (e.key === '`' || e.key === 'ё') panel?.toggle();
  });

  let statsAt = 0;
  /**
   * Страховка на эфир: одно исключение в кадре — и дальше оно повторяется
   * каждый кадр, картинка застывает чёрной, а ведущий узнаёт об этом от
   * зрителей. Перезагрузка возвращает бой за секунду. Метку ставим в
   * sessionStorage, чтобы не уйти в вечный цикл перезагрузок на битой сборке.
   */
  const RELOAD_MARK = 'rekrut:crash-reload';
  const onFatal = (e: unknown): void => {
    console.error('[rekrut] кадр упал', e);
    const last = Number(sessionStorage.getItem(RELOAD_MARK) ?? 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem(RELOAD_MARK, String(Date.now()));
    location.reload();
  };

  app.ticker.add((ticker) => {
    const dt = Math.min(ticker.deltaMS, 100);
    try {
      // Генератор идёт тем же временем, что и симуляция: на ускорении ×4
      // события должны ускоряться вместе с боем.
      source.tick?.(dt * speed);
      engine.update(dt, speed);
      renderer.render(dt);
    } catch (e) {
      onFatal(e);
      return;
    }

    statsAt += dt;
    if (statsAt >= 250) {
      panel?.update(statsAt);
      statsAt = 0;
    }
  });

  // Страховка от накопленных утечек на суточном эфире (§11).
  setTimeout(() => location.reload(), 6 * 60 * 60 * 1000);

  // applyEvent наружу — им проверяют игру записью реального эфира.
  Object.assign(window, { engine, renderer, mock, cfg, applyEvent });
}

void main();
